import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProviderAuth } from '@/context/ProviderAuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import PasscodeInput from '@/components/PasscodeInput';
import PhoneInput from '@/components/PhoneInput';
import ImageUploadWithCrop from '@/components/ImageUploadWithCrop';
import { formatPhoneDisplay } from '@/utils/phoneUtils';
import { toast } from 'sonner';
import { 
  Lock, Upload, Plus, X, Award, 
  MapPin, Clock, DollarSign, Image as ImageIcon, CheckCircle 
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SPECIALIZATIONS = [
  'Residential', 'Commercial', 'Emergency Service', 'Licensed', 'Insured', 'Bonded',
  '24/7 Available', 'Free Estimates', 'Warranty Provided'
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export default function ProviderPublicProfile() {
  const navigate = useNavigate();
  const { provider, token } = useProviderAuth();
  const [providerData, setProviderData] = useState(null);
  const [profile, setProfile] = useState({
    photo_url: '',
    email: '',
    phone: '',
    company_name: '',
    service_type: '',
    service_types: [],
    years_of_experience: '',
    certifications: [],
    service_areas: [],
    bio: '',
    website: '',
    social_media: {
      facebook: '',
      instagram: '',
      linkedin: '',
      twitter: ''
    },
    external_reviews_url: '',
    business_hours: 'Mon-Fri: 9AM-5PM',
    emergency_service: false,
    pricing_info: '',
    portfolio_images: [],
    specializations: ['Residential', 'Licensed', 'Free Estimates']
  });
  const [stats, setStats] = useState({
    total_assignments: 0,
    completed_assignments: 0,
    average_rating: 0,
    total_reviews: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showPasscodeDialog, setShowPasscodeDialog] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [hasPasscode, setHasPasscode] = useState(false);

  // New item inputs
  const [newCertification, setNewCertification] = useState('');
  const [newServiceArea, setNewServiceArea] = useState({ city: '', state: 'NJ' });
  const [allServiceTypes, setAllServiceTypes] = useState([]);
  const [newPortfolioImage, setNewPortfolioImage] = useState('');

  const fetchedRef = useRef(false);
  const profileLoadedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchProfile();
  }, []);

  // Auto-save specializations and service_areas
  useEffect(() => {
    if (!profileLoadedRef.current) return;
    const autoSave = async () => {
      try {
        const payload = {
          ...profile,
          years_of_experience: profile.years_of_experience ? parseInt(profile.years_of_experience) : null
        };
        await fetch(`${API}/api/provider/public-profile`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } catch (err) {
        console.error('Auto-save error:', err);
      }
    };
    autoSave();
  }, [profile.specializations, profile.service_areas]);

  const fetchProfile = async () => {
    try {
      // Fetch all master service types
      const typesRes = await fetch(`${API}/api/public/service-types`);
      if (typesRes.ok) {
        setAllServiceTypes(await typesRes.json());
      }

      // Fetch provider data to check if passcode is set
      const providerResponse = await fetch(`${API}/api/provider/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (providerResponse.ok) {
        const providerData = await providerResponse.json();
        setHasPasscode(providerData.has_passcode);
      }
      
      const response = await fetch(`${API}/api/provider/public-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const profileData = data.profile || {};
        
        setProfile({
          photo_url: profileData.photo_url || '',
          email: profileData.email || data.email || provider?.email || '',
          phone: profileData.phone || data.phone || provider?.phone || '',
          company_name: profileData.company_name || data.company_name || provider?.company_name || '',
          service_type: profileData.service_type || data.service_type || provider?.service_type || '',
          service_types: profileData.service_types || data.service_types || provider?.service_types || [],
          years_of_experience: profileData.years_of_experience || '',
          certifications: profileData.certifications || [],
          service_areas: profileData.service_areas || [],
          bio: profileData.bio || '',
          website: profileData.website || '',
          social_media: profileData.social_media || {
            facebook: '', instagram: '', linkedin: '', twitter: ''
          },
          external_reviews_url: profileData.external_reviews_url || '',
          business_hours: profileData.business_hours || '',
          emergency_service: profileData.emergency_service || false,
          pricing_info: profileData.pricing_info || '',
          portfolio_images: profileData.portfolio_images || [],
          specializations: profileData.specializations || []
        });
        profileLoadedRef.current = true;
        
        setStats(data.stats || {
          total_assignments: 0,
          completed_assignments: 0,
          average_rating: 0,
          total_reviews: 0
        });

        setProviderData({
          full_name: data.full_name,
          email: data.email,
          phone: data.phone,
          company_name: data.company_name,
          service_type: data.service_type,
          profile_slug: data.profile_slug
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handlePasscodeSubmit = async () => {
    if (passcode.length !== 6) {
      toast.error('Please enter your 6-digit passcode');
      return;
    }

    try {
      const response = await fetch(`${API}/api/provider/passcode/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ passcode })
      });

      if (response.ok) {
        setIsEditing(true);
        setShowPasscodeDialog(false);
        setPasscode('');
        toast.success('Passcode verified! You can now edit your profile.');
      } else {
        toast.error('Invalid passcode');
      }
    } catch (error) {
      console.error('Error verifying passcode:', error);
      toast.error('Failed to verify passcode');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    fetchProfile(); // Reset to original data
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert years_of_experience to number
      const payload = {
        ...profile,
        years_of_experience: profile.years_of_experience ? parseInt(profile.years_of_experience) : null
      };

      const response = await fetch(`${API}/api/provider/public-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Public profile updated successfully!');
        setIsEditing(false);
        fetchProfile(); // Refresh to get updated stats
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const toggleSpecialization = (spec) => {
    setProfile(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(s => s !== spec)
        : [...prev.specializations, spec]
    }));
  };

  const addCertification = () => {
    if (newCertification.trim()) {
      setProfile(prev => ({
        ...prev,
        certifications: [...prev.certifications, newCertification.trim()]
      }));
      setNewCertification('');
    }
  };

  const removeCertification = (index) => {
    setProfile(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const addServiceArea = () => {
    if (newServiceArea.city.trim() && newServiceArea.state) {
      const titleCity = newServiceArea.city.trim().replace(/\b\w/g, c => c.toUpperCase());
      setProfile(prev => ({
        ...prev,
        service_areas: [...prev.service_areas, { city: titleCity, state: newServiceArea.state }]
      }));
      setNewServiceArea({ city: '', state: 'NJ' });
    } else {
      toast.error('Please enter both city and state');
    }
  };

  const removeServiceArea = (index) => {
    setProfile(prev => ({
      ...prev,
      service_areas: prev.service_areas.filter((_, i) => i !== index)
    }));
  };

  const addPortfolioImage = () => {
    if (newPortfolioImage.trim()) {
      setProfile(prev => ({
        ...prev,
        portfolio_images: [...prev.portfolio_images, newPortfolioImage.trim()]
      }));
      setNewPortfolioImage('');
    }
  };

  const removePortfolioImage = (index) => {
    setProfile(prev => ({
      ...prev,
      portfolio_images: prev.portfolio_images.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-stone-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-stone-900">Public Profile</h2>
          <p className="text-stone-600 mt-1 text-sm">Manage your professional profile visible to agents and clients</p>
        </div>
          <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
            {!isEditing ? (
              <Button 
                onClick={handleEditClick}
                className="bg-[#1A4D2E] hover:bg-[#2d7a4a]"
                size="sm"
              >
                <Lock size={16} className="mr-2" />
                Edit Profile
              </Button>
            ) : (
              <>
                <Button 
                  onClick={handleCancelEdit}
                  variant="outline"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  size="sm"
                  className="bg-[#1A4D2E] hover:bg-[#2d7a4a]"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Passcode Dialog */}
        <Dialog open={showPasscodeDialog} onOpenChange={setShowPasscodeDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[#1A4D2E] flex items-center gap-2">
                <Lock size={24} />
                Enter Your Passcode
              </DialogTitle>
              <DialogDescription>
                Enter your security passcode to edit your public profile.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <PasscodeInput
                  length={6}
                  value={passcode}
                  onChange={setPasscode}
                  autoFocus
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPasscodeDialog(false);
                  setPasscode('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePasscodeSubmit}
                disabled={passcode.length !== 6}
                className="flex-1 bg-[#1A4D2E] hover:bg-[#15381F]"
              >
                Verify
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E]">{stats.total_assignments}</div>
              <p className="text-sm text-stone-600">Total Leads</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E]">{stats.completed_assignments}</div>
              <p className="text-sm text-stone-600">Completed Jobs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E]">{stats.average_rating.toFixed(1)}</div>
              <p className="text-sm text-stone-600">Average Rating</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E]">{stats.total_reviews}</div>
              <p className="text-sm text-stone-600">Client Reviews</p>
            </CardContent>
          </Card>
        </div>

        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Your professional details and contact information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Profile Photo Upload with Crop - Rectangle shape for Provider */}
            <ImageUploadWithCrop
              currentImage={profile.photo_url}
              onImageChange={(imageData) => setProfile({...profile, photo_url: imageData})}
              shape="rectangle"
              disabled={!isEditing}
              label="Profile Photo"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  name="email"
                  value={profile.email}
                  disabled
                  placeholder="Not provided"
                  className="bg-stone-100"
                />
                <p className="text-xs text-stone-500 mt-1">Email cannot be changed from public profile</p>
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  value={formatPhoneDisplay(profile.phone)}
                  disabled
                  placeholder="Not provided"
                  className="bg-stone-100"
                />
                <p className="text-xs text-stone-500 mt-1">Phone cannot be changed from public profile</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Company Name</Label>
                <Input
                  value={profile.company_name}
                  onChange={(e) => setProfile({...profile, company_name: e.target.value})}
                  placeholder="e.g., ABC Plumbing Services"
                  disabled={!isEditing}
                />
              </div>
              <div>
                <Label>Service Types</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {profile.service_types.length > 0 ? profile.service_types.map((t) => (
                    <span key={t} className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">{t}</span>
                  )) : (
                    <span className="text-sm text-stone-500">{profile.service_type || 'Not set'}</span>
                  )}
                </div>
                <p className="text-xs text-stone-500 mt-1">Manage service types in Settings</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Years of Experience</Label>
                <Input
                  type="number"
                  value={profile.years_of_experience}
                  onChange={(e) => setProfile({...profile, years_of_experience: e.target.value})}
                  placeholder="e.g., 10"
                  disabled={!isEditing}
                />
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={profile.website}
                  onChange={(e) => setProfile({...profile, website: e.target.value})}
                  placeholder="https://yourwebsite.com"
                  disabled={!isEditing}
                />
              </div>
            </div>

            {/* Social Media Links */}
            <div className="space-y-2">
              <Label>Social Media Links</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-stone-600">Facebook</Label>
                  <div className="flex items-center">
                    <span className="px-3 py-2 bg-stone-100 border border-r-0 border-stone-300 rounded-l-md text-sm text-stone-600">
                      facebook.com/
                    </span>
                    <Input
                      value={profile.social_media.facebook}
                      onChange={(e) => setProfile({
                        ...profile,
                        social_media: {...profile.social_media, facebook: e.target.value}
                      })}
                      placeholder="username"
                      disabled={!isEditing}
                      className="rounded-l-none"
                    />
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs text-stone-600">Instagram</Label>
                  <div className="flex items-center">
                    <span className="px-3 py-2 bg-stone-100 border border-r-0 border-stone-300 rounded-l-md text-sm text-stone-600">
                      instagram.com/
                    </span>
                    <Input
                      value={profile.social_media.instagram}
                      onChange={(e) => setProfile({
                        ...profile,
                        social_media: {...profile.social_media, instagram: e.target.value}
                      })}
                      placeholder="username"
                      disabled={!isEditing}
                      className="rounded-l-none"
                    />
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs text-stone-600">LinkedIn</Label>
                  <div className="flex items-center">
                    <span className="px-3 py-2 bg-stone-100 border border-r-0 border-stone-300 rounded-l-md text-sm text-stone-600">
                      linkedin.com/in/
                    </span>
                    <Input
                      value={profile.social_media.linkedin}
                      onChange={(e) => setProfile({
                        ...profile,
                        social_media: {...profile.social_media, linkedin: e.target.value}
                      })}
                      placeholder="username"
                      disabled={!isEditing}
                      className="rounded-l-none"
                    />
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs text-stone-600">Twitter (X)</Label>
                  <div className="flex items-center">
                    <span className="px-3 py-2 bg-stone-100 border border-r-0 border-stone-300 rounded-l-md text-sm text-stone-600">
                      twitter.com/
                    </span>
                    <Input
                      value={profile.social_media.twitter}
                      onChange={(e) => setProfile({
                        ...profile,
                        social_media: {...profile.social_media, twitter: e.target.value}
                      })}
                      placeholder="username"
                      disabled={!isEditing}
                      className="rounded-l-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-1 mt-4">
              <Label className="text-xs text-stone-600">External Reviews</Label>
              <div className="flex gap-2">
                <Input
                  value={profile.external_reviews_url || ''}
                  onChange={(e) => setProfile({...profile, external_reviews_url: e.target.value})}
                  placeholder="https://g.page/your-business/review or any review link"
                  disabled={!isEditing}
                  className="flex-1"
                />
                {!isEditing && profile.external_reviews_url && (
                  <a href={profile.external_reviews_url.startsWith('http') ? profile.external_reviews_url : `https://${profile.external_reviews_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 transition-colors whitespace-nowrap" data-testid="external-reviews-link">
                    Open Link
                  </a>
                )}
              </div>
              <p className="text-xs text-stone-400">Google Reviews, Yelp, or any external review page URL</p>
            </div>
          </CardContent>
        </Card>

        {/* About & Services */}
        <Card>
          <CardHeader>
            <CardTitle>About Your Business</CardTitle>
            <CardDescription>Tell clients about your services and expertise</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Professional Bio</Label>
              <Textarea
                value={profile.bio}
                onChange={(e) => setProfile({...profile, bio: e.target.value})}
                placeholder="Write a compelling description of your business and services..."
                rows={6}
                disabled={!isEditing}
              />
              <p className="text-xs text-stone-500 mt-1">
                {profile.bio.length}/1000 characters
              </p>
            </div>

            <div>
              <Label>Specializations</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {SPECIALIZATIONS.map(spec => (
                  <button
                    key={spec}
                    onClick={() => isEditing && toggleSpecialization(spec)}
                    disabled={!isEditing}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      profile.specializations.includes(spec)
                        ? 'bg-[#1A4D2E] text-white'
                        : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                    } ${!isEditing ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                  >
                    {spec}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Business Hours</Label>
                <Input
                  value={profile.business_hours}
                  onChange={(e) => setProfile({...profile, business_hours: e.target.value})}
                  placeholder="e.g., Mon-Fri: 9AM-5PM"
                  disabled={!isEditing}
                />
              </div>
              <div>
                <Label>Pricing Information</Label>
                <Input
                  value={profile.pricing_info}
                  onChange={(e) => setProfile({...profile, pricing_info: e.target.value})}
                  placeholder="e.g., Starting at $50/hour"
                  disabled={!isEditing}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="emergency"
                checked={profile.emergency_service}
                onChange={(e) => setProfile({...profile, emergency_service: e.target.checked})}
                disabled={!isEditing}
                className="w-4 h-4"
              />
              <Label htmlFor="emergency" className="cursor-pointer">
                24/7 Emergency Service Available
              </Label>
            </div>

            <div>
              <Label>Certifications & Licenses</Label>
              {isEditing && (
                <div className="flex gap-2 mt-2">
                  <Input
                    value={newCertification}
                    onChange={(e) => setNewCertification(e.target.value)}
                    placeholder="e.g., Licensed, Insured, Bonded"
                    onKeyPress={(e) => e.key === 'Enter' && addCertification()}
                  />
                  <Button onClick={addCertification} variant="outline">
                    <Plus size={16} />
                  </Button>
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-3">
                {profile.certifications.map((cert, idx) => (
                  <div key={idx} className="flex items-center gap-1 bg-amber-100 text-amber-800 px-3 py-1 rounded-full">
                    <Award size={14} />
                    <span className="text-sm">{cert}</span>
                    {isEditing && (
                      <button onClick={() => removeCertification(idx)} className="ml-1">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Areas */}
        <Card>
          <CardHeader>
            <CardTitle>Service Areas</CardTitle>
            <CardDescription>Cities and regions where you provide services</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing && (
              <div className="flex gap-2">
                <Input
                  value={newServiceArea.city}
                  onChange={(e) => setNewServiceArea({...newServiceArea, city: e.target.value})}
                  placeholder="City"
                  className="flex-1"
                />
                <Select value={newServiceArea.state} onValueChange={(val) => setNewServiceArea({...newServiceArea, state: val})}>
                  <SelectTrigger className="w-24">
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={addServiceArea} variant="outline">
                  <Plus size={16} />
                </Button>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {profile.service_areas.map((area, idx) => (
                <div key={idx} className="flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full">
                  <MapPin size={14} />
                  <span className="text-sm">{area.city}, {area.state}</span>
                  {isEditing && (
                    <button onClick={() => removeServiceArea(idx)} className="ml-1">
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Portfolio */}
        <Card>
          <CardHeader>
            <CardTitle>Portfolio / Past Projects</CardTitle>
            <CardDescription>Showcase your work with project images</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing && (
              <div className="flex gap-2">
                <Input
                  value={newPortfolioImage}
                  onChange={(e) => setNewPortfolioImage(e.target.value)}
                  placeholder="Image URL (e.g., https://example.com/project1.jpg)"
                  className="flex-1"
                />
                <Button onClick={addPortfolioImage} variant="outline">
                  <Plus size={16} />
                </Button>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              {profile.portfolio_images.map((imgUrl, idx) => (
                <div key={idx} className="relative group">
                  <div className="aspect-square rounded-lg overflow-hidden border-2 border-stone-200">
                    <img 
                      src={imgUrl} 
                      alt={`Portfolio ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23e5e7eb" width="200" height="200"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239ca3af">No Image</text></svg>';
                      }}
                    />
                  </div>
                  {isEditing && (
                    <button
                      onClick={() => removePortfolioImage(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {profile.portfolio_images.length === 0 && (
              <div className="text-center py-8 text-stone-500">
                <ImageIcon size={48} className="mx-auto mb-2 opacity-50" />
                <p>No portfolio images yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
  );
}
